import React, { FC } from 'react';
import { Row } from 'antd';
import styled from 'styled-components';
import { RichText } from '@graphcms/rich-text-react-renderer';
import { H8CamptonSemiBold20, GreenLinkText } from '../../../../layout/globalFonts';
import { PartnersMkLinksTo, ProductInfo, Service } from '../../../../swagger/api/index';
import ContentStore from '../../../../stores/ContentStore';
import ConfirmOpenOresundLinkModal from '../modals/ConfirmOpenOresundLinkModal';
import NoIdentifierFoundWaitAMoment from '../modals/NoIdentifierFoundWaitAMoment';
import CheckmarkLabel from './CheckmarkLabel';

const HeaderSection = styled(Row)`
  padding-bottom: 10px;
`;

const StyledRow = styled(Row)`
  flex-direction: column;
  gap: 12px;
  line-height: 1.2;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const LinksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
  padding-top: 15px;
`;

const StyledLink = styled(GreenLinkText)`
  display: block;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

interface LocalServicesProps {
  product: ProductInfo;
  contentStore: ContentStore;
}

const LocalServices: FC<LocalServicesProps> = (props) => {
  const { product, contentStore } = props;
  const { loadingStore, modalStore } = contentStore.rootStore;
  const { productCardStore } = contentStore.rootStore.productStore;

  const getPartnerLink = (partner: PartnersMkLinksTo) => {
    loadingStore.enableGlobalSpinner();
    productCardStore
      .getPartnerLink(partner)
      .then((url) => {
        loadingStore.disableGlobalSpinner();
        modalStore.openModal(
          <ConfirmOpenOresundLinkModal
            closeModal={modalStore.closeModal}
            oresundUrl={url!}
            contentStore={contentStore}
          />
        );
      })
      .catch((error) => {
        if (error.response?.data.errorCode.includes('NoIdentifierFoundByPartner')) {
          modalStore.openModal(
            <NoIdentifierFoundWaitAMoment modalStore={modalStore} contentStore={contentStore} />
          );
        }
      })
      .finally(() => loadingStore.disableGlobalSpinner());
  };

  const agreement = (service: Service) => {
    const serviceSystemId = contentStore.cmsStore.getContent(service.name!)?.content;
    return (
      serviceSystemId !== undefined &&
      service.isActive && (
        <CheckmarkLabel>
          {contentStore.cmsStore.getContent(`${serviceSystemId}OnProductCard`)?.content}
        </CheckmarkLabel>
      )
    );
  };

  const agreementRows = product.servicesOnIdentifier
    ?.filter(
      (service: Service) =>
        service.isActive && contentStore.cmsStore.getContent(service.name!)?.content
    )
    .map((service: Service) => agreement(service));

  const getOresundDiscountLink = () =>
    !product.servicesOnIdentifier?.some(
      (s: Service) =>
        (contentStore.cmsStore.getContent(s.name!)?.content === 'OresundServiceAgreement' ||
          contentStore.cmsStore.getContent(s.name!)?.content ===
            'OeresundCommercialServiceAgreement') &&
        s.isActive !== false
    ) && (
      <StyledLink
        style={{ cursor: 'pointer' }}
        onClick={() => getPartnerLink(PartnersMkLinksTo.BropassOresund)}
      >
        {contentStore.cmsStore.getContent('OresundServiceAgreementGetDiscountLinkName')?.content}
      </StyledLink>
    );

  return (
    <StyledContainer>
      <HeaderSection>
        <H8CamptonSemiBold20>
          {contentStore.cmsStore.getContent('LocalServices')?.content}
        </H8CamptonSemiBold20>
      </HeaderSection>
      <ContentSection>
        {!!agreementRows && agreementRows.length > 0 && <StyledRow>{agreementRows}</StyledRow>}
      </ContentSection>
      <LinksSection>
        {getOresundDiscountLink()}
        <GreenLinkText>
          <RichText
            content={
              contentStore.cmsStore.getContent('MoreAboutLocalServices')?.richContent?.raw ?? []
            }
            renderers={{
              p: ({ children }) => <p style={{ marginBottom: '0px' }}>{children}</p>
            }}
          />
        </GreenLinkText>
      </LinksSection>
    </StyledContainer>
  );
};

export default LocalServices;
